import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FileText, Plus } from 'lucide-react'
import { Page } from '@/components/Page'
import { DataTable } from '@/components/DataTable'
import { Toolbar } from '@/components/Toolbar'
import { EmptyState } from '@/components/EmptyState'
import { FormField } from '@/components/FormField'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/auth/useAuth'
import { useInvoices } from '@/features/invoicing/queries'
import { formatInvoiceMoney } from '@/features/invoicing/format'
import type { InvoiceListItem, InvoiceStatus } from '@/features/invoicing/types'
import { formatDate } from '@/lib/locale'
import { PostingBanner } from '@/components/PostingBanner'
import { PostingSelectorModal, type PostingSelectorItem } from '@/components/PostingSelectorModal'
import { usePostingStatus, usePostSelected } from '@/features/accruals/queries'
import { useUnpostedPreview } from '@/features/reporting/queries'
import { useToast } from '@/hooks/useToast'

const STATUS_VARIANT: Record<InvoiceStatus, 'default' | 'secondary' | 'danger' | 'outline' | 'success' | 'warning' | 'info'> = {
  draft: 'secondary',
  sent: 'default',
  viewed: 'default',
  partially_paid: 'default',
  paid: 'success',
  overdue: 'danger',
  void: 'secondary',
  canceled: 'secondary',
  written_off: 'secondary',
}

export function InvoicesListPage() {
  const { hasPermission } = useAuth()
  const navigate = useNavigate()
  const [status, setStatus] = useState<InvoiceStatus | ''>('')
  const [search, setSearch] = useState('')

  const { data, isLoading } = useInvoices(status ? { status: status as InvoiceStatus } : undefined)

  const rows = useMemo(() => {
    const items = data?.invoices ?? []
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(i =>
      i.invoice_number.toLowerCase().includes(q)
      || (i.customer_display_name || '').toLowerCase().includes(q)
    )
  }, [data, search])

  const columns = useMemo(() => [
    {
      accessorKey: 'invoice_number',
      header: 'Invoice #',
      cell: ({ row }: { row: { original: InvoiceListItem } }) => (
        <div className="flex flex-col">
          <Link to={`/invoicing/invoices/${row.original.id}`} className="font-medium text-primary hover:underline">
            {row.original.invoice_number}
          </Link>
          {row.original.po_number ? (
            <span className="text-[0.7rem] text-muted-foreground">PO: {row.original.po_number}</span>
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: 'customer_display_name',
      header: 'Customer',
    },
    {
      accessorKey: 'issue_date',
      header: 'Issue',
      cell: ({ row }: { row: { original: InvoiceListItem } }) => formatDate(row.original.issue_date),
    },
    {
      accessorKey: 'due_date',
      header: 'Due',
      cell: ({ row }: { row: { original: InvoiceListItem } }) => formatDate(row.original.due_date),
    },
    {
      accessorKey: 'total',
      header: 'Total',
      cell: ({ row }: { row: { original: InvoiceListItem } }) =>
        formatInvoiceMoney(row.original.total, row.original.currency),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: { row: { original: InvoiceListItem } }) => {
        const s = row.original.status
        return <Badge variant={STATUS_VARIANT[s]}>{s.replace(/_/g, ' ')}</Badge>
      },
    },
  ], [])

  const canCreate = hasPermission('invoicing.invoices.create')

  // Posting banner - appears when there are AR invoices that haven't
  // been posted to the GL yet (manual mode keeps them in this state
  // until the user clicks Post Now).
  const posting = usePostingStatus()
  const postSelectedMut = usePostSelected()
  const { toast } = useToast()
  const [postSelectorOpen, setPostSelectorOpen] = useState(false)
  const [selectorReadOnly, setSelectorReadOnly] = useState(false)
  const unpostedPreview = useUnpostedPreview(postSelectorOpen)
  const unpostedInvoices = posting.data?.unpostedInvoices ?? 0

  const invoiceItems = useMemo<PostingSelectorItem[]>(() => {
    const rows = unpostedPreview.data?.invoices ?? []
    return rows.map((r) => ({
      id: r.id,
      cells: [
        formatDate(r.issueDate),
        <span className="font-mono text-xs">{r.invoiceNumber || `INV #${r.id}`}</span>,
        r.customerName || '-',
        r.status ? <Badge variant="secondary" className="capitalize">{String(r.status).replace(/_/g, ' ')}</Badge> : '-',
        formatInvoiceMoney(r.total, r.currency),
      ],
      blocked: r.blocked,
      blockedReason: r.blocked ? 'Reporting period is closed' : undefined,
    }))
  }, [unpostedPreview.data])

  const invoiceColumns = [
    { header: 'Date', width: 'w-32' },
    { header: 'Invoice #', width: 'w-32' },
    { header: 'Customer' },
    { header: 'Status', width: 'w-32' },
    { header: 'Total', align: 'right' as const, width: 'w-28' },
  ]

  const runPostInvoices = async (selection: Record<string, number[]>) => {
    const ids = selection.invoices ?? []
    if (ids.length === 0) return
    try {
      const r = await postSelectedMut.mutateAsync({ source: 'invoices', ids })
      toast({
        title: 'Posted to GL',
        description: `Posted ${r.posted ?? 0} invoice${(r.posted ?? 0) === 1 ? '' : 's'}.`,
      })
      setPostSelectorOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Posting failed',
        description: err instanceof Error ? err.message : 'Try again.',
      })
    }
  }

  return (
    <Page
      title="Invoices"
      description="Track and collect money due from customers. New invoices wait for an explicit Post Now click unless you switch invoices to auto-post in Company → Posting Config."
      actions={canCreate ? (
        <Button onClick={() => navigate('/invoicing/invoices/new')}>
          <Plus className="mr-2 size-4" /> New invoice
        </Button>
      ) : undefined}
    >
      <PostingBanner
        count={unpostedInvoices}
        label="invoices"
        singularLabel="invoice"
        source="invoices"
        onView={() => { setSelectorReadOnly(true); setPostSelectorOpen(true) }}
        onPostNow={() => { setSelectorReadOnly(false); setPostSelectorOpen(true) }}
        busy={postSelectedMut.isPending}
      />

      <PostingSelectorModal
        open={postSelectorOpen}
        onOpenChange={setPostSelectorOpen}
        title={selectorReadOnly ? 'Pending invoices' : 'Post invoices to GL'}
        description={selectorReadOnly
          ? 'These invoices are queued to post. Click Post Now to choose which to post.'
          : 'Tick the invoices you want to post. Posting only affects the rows you select - bills, accruals, and other sources are untouched.'}
        readOnly={selectorReadOnly}
        onSwitchToPost={() => setSelectorReadOnly(false)}
        loading={unpostedPreview.isLoading}
        posting={postSelectedMut.isPending}
        tabs={[
          {
            key: 'invoices',
            label: 'Invoices',
            items: invoiceItems,
            columns: invoiceColumns,
            emptyMessage: 'No invoices are pending GL posting.',
          },
        ]}
        onPost={runPostInvoices}
      />

      <Toolbar align="start" className="mb-2">
        <FormField
          label=""
          placeholder="Search invoice # or customer"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-64"
        />
        <select
          value={status}
          onChange={e => setStatus(e.target.value as InvoiceStatus | '')}
          className="h-10 rounded-md border bg-card px-3 text-sm"
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="viewed">Viewed</option>
          <option value="partially_paid">Partially paid</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="void">Void</option>
        </select>
      </Toolbar>
      <DataTable
        columns={columns as never}
        rows={rows}
        loading={isLoading}
        empty={
          <EmptyState
            icon={FileText}
            title="No invoices yet"
            description={canCreate ? 'Create your first invoice to start getting paid.' : 'No invoices match your filters.'}
            action={canCreate ? <Button onClick={() => navigate('/invoicing/invoices/new')}><Plus className="mr-2 size-4" />Create invoice</Button> : null}
          />
        }
      />
    </Page>
  )
}
